import React, { useCallback, useMemo, useState, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons/faTimesCircle';

import { AgencyAvailabilityAdminService, CsvParserService } from 'us-web-services';
import { Table, TableInstance, CreateModal } from 'src/components/common/Table';
import DisplayService from '../../../util/DisplayService';
import PageStyles from '../../../styles/PageStyles';
import useAuthentication from '../../../store/useAuthentication';

function AgencyAvailabilityManager() {
  const [authenticationState] = useAuthentication();
  const authenticatedUser = authenticationState.authentication.user;
  const isAgencyAdmin = authenticatedUser.roles.filter(role => role.code === 'AGENCY_ADMIN').length > 0;

  const [file, setFile] = useState(null);
  const tableRef = useRef<TableInstance>();
  const [parsed, setParsed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(null);
  const [validCount, setValidCount] = useState(null);
  const [duplicateCount, setDuplicateCount] = useState(null);
  const [invalidAgencyCount, setInvalidAgencyCount] = useState(null);
  const [invalidZipCount, setInvalidZipCount] = useState(null);
  const [invalidAvailabilityCount, setInvalidAvailabilityCount] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [addedCount, setAddedCount] = useState(0);
  const [removedCount, setRemovedCount] = useState(0);

  const fileInput = useRef<HTMLInputElement>();
  const { classes } = PageStyles();

  const openConfirm = () => {
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    setShowConfirm(false);
  };

  const showError = useCallback(error => {
    const displayedError = DisplayService.getErrorResponse(
      error,
      'There was an unknown error updating the agency availability.',
      false,
    );

    displayedError.autoDismiss = false;
    toast.error(displayedError.message);
    setUploadSuccessful(false);
  }, []);

  const resetAfterUpload = (response) => {
    const responseData = response.data.data;

    setFile(null);
    setParsed(false);
    setValidCount(null);
    setDuplicateCount(null);
    setInvalidAgencyCount(null);
    setInvalidZipCount(null);
    setInvalidAvailabilityCount(null);
    setUploadSuccessful(responseData.success);
    setAddedCount(responseData.addedCount || 0);
    setRemovedCount(responseData.removedCount || 0);

    if (responseData.errors && responseData.errors.length > 0) {
      const formattedErrors = responseData.errors.map(err => ({
        row: err.row,
        error: err.message,
      }));

      setValidationErrors(formattedErrors);
    } else {
      setValidationErrors([]);
    }
  };

  const getNumberOfDuplicatesInCSV = data => {
    const zipCodes = data.map(row => row[1]);
    const uniqueZipCodes = new Set(zipCodes);

    return zipCodes.length - uniqueZipCodes.size;
  };

  const validateAgencyId = (agencyId) => {
    if (agencyId.trim() === '') {
      return { valid: true };
    }

    if (Number.isNaN(Number(agencyId)) || !Number.isInteger(Number(agencyId)) || Number(agencyId) <= 0) {
      return { valid: false, reason: 'Agency ID must be a positive integer' };
    }

    return { valid: true };
  };

  const validateAvailability = (availability) => {
    const normalized = String(availability).trim().toLowerCase();

    if ([
      'true', 'false', 'yes', 'no', '1', '0',
      'on', 'off', 'enable', 'disable', 'enabled', 'disabled',
    ].includes(normalized)) {
      return { valid: true };
    }

    return { valid: false, reason: 'Availability must be true/false, yes/no, 1/0, on/off, or enable/disable' };
  };

  const processData = async (data) => {
    const startIndex = data[0][0]?.toLowerCase() === 'agency_id' ? 1 : 0;
    const dataRows = data.slice(startIndex);

    let valid = 0;
    const invalid = { agencyCount: 0, zipCount: 0, availabilityCount: 0 };
    const errors = [];

    const duplicates = getNumberOfDuplicatesInCSV(dataRows);

    if (duplicates > 0) {
      errors.push({ row: 'N/A', error: `Found ${duplicates} duplicate zip codes in the CSV` });
    }

    dataRows.forEach((row, index) => {
      const rowNum = startIndex + index + 1;

      if (!row || !row[0]) {
        invalid.zipCount += 1;
        errors.push({
          row: rowNum,
          error: 'Empty or invalid row detected.',
        });

        return;
      }

      if (!row || row.length < 3) {
        errors.push({
          row: rowNum,
          error: 'Invalid row: Each row must contain agency_id, zip_code, and availability.',
        });

        return;
      }

      if (row[0]?.trim() !== '') {
        const agencyValidation = validateAgencyId(row[0] || '');

        if (!agencyValidation.valid) {
          invalid.agencyCount += 1;
          errors.push({
            row: rowNum,
            error: `Invalid agency ID: ${row[0]}. ${agencyValidation.reason}.`,
          });

          return;
        }
      }

      if (!row[1] || row[1].trim() === '') {
        invalid.zipCount += 1;
        errors.push({
          row: rowNum,
          error: 'Missing zip code in column 2.',
        });

        return;
      }

      const availabilityValidation = validateAvailability(row[2] || '');

      if (!availabilityValidation.valid) {
        invalid.availabilityCount += 1;
        errors.push({
          row: rowNum,
          error: `Invalid availability value: ${row[2]}. ${availabilityValidation.reason}.`,
        });

        return;
      }

      valid += 1;
    });

    setValidCount(valid);
    setDuplicateCount(duplicates);
    setInvalidAgencyCount(invalid.agencyCount);
    setInvalidZipCount(invalid.zipCount);
    setInvalidAvailabilityCount(invalid.availabilityCount);
    setValidationErrors(errors);
    setParsed(true);
  };

  const processFile = async () => {
    setParsed(false);
    setValidCount(null);
    setDuplicateCount(null);
    setInvalidAgencyCount(null);
    setInvalidZipCount(null);
    setInvalidAvailabilityCount(null);
    setValidationErrors([]);
    setUploadSuccessful(null);
    setAddedCount(0);
    setRemovedCount(0);

    if (fileInput.current.files[0]) {
      setUploadSuccessful(null);
      setFile(fileInput.current.files[0]);
      const reader = new window.FileReader();

      reader.onload = async (e) => {
        const data = CsvParserService.parse(e.target.result.toString(), ',', false);

        await processData(data);
      };
      reader.readAsText(fileInput.current.files[0]);
    } else {
      toast.error('Uploaded file does not exist');
    }
  };

  const refreshAgencyAvailability = () => tableRef.current?.fetchData();

  const uploadFile = async () => {
    if (file) {
      const formData = new FormData();

      formData.append('file', file);

      setShowConfirm(false);
      setUploading(true);

      await AgencyAvailabilityAdminService
        .upload(formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(resetAfterUpload)
        .catch(showError)
        .finally(() => {
          setUploading(false);
          refreshAgencyAvailability();
        });
    }
  };

  const addAgencyAvailability = async (values) => {
    const data = {
      agencyId: values.agencyId,
      zipCode: values.zipCode,
    };

    await AgencyAvailabilityAdminService.create(data);
    refreshAgencyAvailability();
  };

  const deleteAgencyAvailability = async (entry) => {
    await AgencyAvailabilityAdminService.delete(entry.id);
    refreshAgencyAvailability();
  };

  const downloadTemplate = () => {
    const csvContent = 'agency_id,zip_code,is_available\n'
      + '5,12345,true\n'
      + '5,67890,false\n'
      + ',54321,true';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', 'agency_availability_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const COLUMNS = useMemo(
    () => [
      {
        id: 'agencyAvailability',
        header: 'Agency Availability by Zip Code',
        columns: [
          {
            header: 'Agency ID',
            accessorKey: 'agencyId',
          },
          {
            header: 'Zip Code',
            accessorKey: 'zipCode',
          },
        ],
      },
    ],
    [],
  );

  if (!isAgencyAdmin) {
    return (
      <Paper className={classes.paper}>
        <Typography variant='h6' color='error'>
          Access Denied
        </Typography>
        <Typography>
          You need the agency_admin role to access this tool.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Dialog open={showConfirm} onClose={closeConfirm}>
        <DialogTitle>Ready to upload?</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to update the agency availability with these {validCount} entries?
          </p>
          <p>
            This will update zip code availability for agencies. Existing entries will be removed and replaced.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={uploadFile}
            disabled={
              uploading
              || validCount === 0
              || duplicateCount > 0
              || invalidAgencyCount > 0
              || invalidZipCount > 0
              || invalidAvailabilityCount > 0
            }
            color='primary'
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Typography variant='h6' gutterBottom>
        Manage Agency Availability by Zip Code
      </Typography>
      <p>
        Upload a .csv file to update the availability of agencies by zip code.
        If a zip code row exists in the CSV with &quot;true&quot;,
        it will be added to the agency&apos;s available zip codes.
        If a zip code row exists with &quot;false&quot;, it will be removed from the agency&apos;s available zip codes.
      </p>
      <p>
        <Button
          color='primary'
          onClick={downloadTemplate}
          variant='outlined'
          style={{ marginBottom: '1rem' }}
        >
          Download CSV Template
        </Button>
      </p>
      <Grid container spacing={2}>
        <Grid item lg={4}>
          <Button
            variant='contained'
            component='label'
            color='primary'
          >
            Select CSV file
            <input
              id='csv-file-agency-availability'
              type='file'
              hidden
              ref={fileInput}
              onChange={() => {
                processFile();
              }}
              onClick={(e) => {
                e.currentTarget.value = '';
              }}
              accept='.csv'
            />
          </Button>
          {file &&
            <Typography variant='body2' style={{ marginTop: 8 }}>
              Selected file: {file.name}
            </Typography>
          }
        </Grid>
        <Grid item lg={4}>
          {parsed && (
            <Box>
              Valid entries: {validCount}
            </Box>
          )}
          {parsed && duplicateCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {duplicateCount}{' '}
              {duplicateCount === 1 ? 'duplicate zip code' : 'duplicate zip codes'} in CSV
            </Box>
          )}
          {parsed && invalidAgencyCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {invalidAgencyCount} invalid agency {invalidAgencyCount === 1 ? 'ID' : 'IDs'}
            </Box>
          )}
          {parsed && invalidZipCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {invalidZipCount} invalid zip {invalidZipCount === 1 ? 'code' : 'codes'}
            </Box>
          )}
          {parsed && invalidAvailabilityCount > 0 && (
            <Box
              style={{
                color: 'red',
                fontWeight: 'bold',
              }}
            >
              {invalidAvailabilityCount} invalid availability {invalidAvailabilityCount === 1 ? 'value' : 'values'}
            </Box>
          )}
        </Grid>
        <Grid item lg={4}>
          {parsed && (
            <Button
              color='primary'
              disabled={
                uploading
                || validCount === 0
                || duplicateCount > 0
                || invalidAgencyCount > 0
                || invalidZipCount > 0
                || invalidAvailabilityCount > 0
              }
              onClick={openConfirm}
              variant='contained'
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          )}
          {uploadSuccessful === true && (
            <div style={{ color: 'green' }}>
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faCheckCircle}
              />
              <span>
                Upload successful!
                {addedCount > 0 && ` Added ${addedCount} zip code${addedCount > 1 ? 's' : ''}.`}
                {removedCount > 0 && ` Removed ${removedCount} zip code${removedCount > 1 ? 's' : ''}.`}
              </span>
            </div>
          )}
          {uploadSuccessful === false && (
            <div style={{ color: 'red' }}>
              <FontAwesomeIcon
                style={{
                  fontSize: 16,
                  marginRight: '0.75rem',
                }}
                icon={faTimesCircle}
              />
              <span>Error occurred</span>
            </div>
          )}
        </Grid>
      </Grid>

      {validationErrors.length > 0 && (
        <Box mt={2} mb={2}>
          <Typography variant='h6' color='error'>
            Validation Errors
          </Typography>
          <Paper style={{ maxHeight: 200, overflow: 'auto', padding: '1rem' }}>
            {validationErrors.map((error, index) => (
              <Box key={index} mb={1}>
                <Typography color='error'>
                  {error.row !== 'N/A' ? `Row ${error.row}: ` : ''}{error.error}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      <Table
        columns={COLUMNS}
        ref={tableRef}
        retrieveData={AgencyAvailabilityAdminService.findByFilter}
        addAction={() => setCreateModalOpen(true)}
        deleteAction={deleteAgencyAvailability}
      />
      <CreateModal
        columns={COLUMNS}
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={addAgencyAvailability}
        title='Add Agency Availability'
        buttonText='Add'
      />
    </Paper>
  );
}

export default withRouter(AgencyAvailabilityManager);
